// src/components/CollectionHeritageAudit.jsx
// Vue d'audit patrimoniale d'un ensemble : œuvres dont le propriétaire, le statut, la provenance ou la
// pièce justificative ne sont pas assez renseignés. Affichée seulement quand l'ensemble a une nature
// (collection personnelle ou de société, archives, estate) ou que des œuvres ont un statut patrimonial.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

const NATURE_LABELS = {
  personal_collection: "Collection personnelle",
  company_collection: "Collection de société",
  gallery_archive: "Archives de galerie",
  artist_estate: "Estate d'artiste",
};

const COPY = {
  fr: {
    eyebrow: "Audit patrimonial", title: "Informations à clarifier",
    allGood: "Rien à signaler : propriétaire, statut, provenance et pièces sont renseignés pour chaque œuvre.",
    subtitle: "Repères pour préparer la continuité ou la transmission ; ce n'est pas une qualification juridique.",
    checks: { status: "Statut patrimonial non renseigné ou à clarifier", owner: "Propriétaire à vérifier", provenance: "Provenance non renseignée", proof: "Aucune pièce justificative (facture ou document)" },
    more: (n) => `… et ${n} autre(s)`,
    nature: NATURE_LABELS,
  },
  en: {
    eyebrow: "Heritage audit", title: "Information to clarify",
    allGood: "Nothing to report: owner, status, provenance and supporting documents are filled in for every work.",
    subtitle: "Pointers to prepare continuity or transmission; this is not a legal qualification.",
    checks: { status: "Heritage status missing or to be clarified", owner: "Owner to be verified", provenance: "Provenance not provided", proof: "No supporting document (invoice or document)" },
    more: (n) => `… and ${n} more`,
    nature: { personal_collection: "Personal collection", company_collection: "Company collection", gallery_archive: "Gallery archive", artist_estate: "Artist estate" },
  },
  de: {
    eyebrow: "Bestandsprüfung", title: "Zu klärende Angaben",
    allGood: "Nichts zu melden: Eigentümer, Status, Provenienz und Belege sind für jedes Werk erfasst.",
    subtitle: "Anhaltspunkte zur Vorbereitung von Kontinuität oder Übertragung; keine rechtliche Einordnung.",
    checks: { status: "Besitzstatus fehlt oder ist zu klären", owner: "Eigentümer zu überprüfen", provenance: "Provenienz nicht angegeben", proof: "Kein Beleg (Rechnung oder Dokument)" },
    more: (n) => `… und ${n} weitere`,
    nature: { personal_collection: "Private Sammlung", company_collection: "Firmensammlung", gallery_archive: "Galeriearchiv", artist_estate: "Künstlernachlass" },
  },
};

const hasText = (v) => typeof v === "string" ? v.trim() !== "" : v != null && v !== false;
const hasDocuments = (a) => {
  const d = a?.documents;
  if (d == null || d === "" || d === "[]") return false;
  return true;
};

const CHECKS = [
  {
    key: "status",
    label: "Statut patrimonial non renseigné ou à clarifier",
    test: (a) => !a.heritage_status || a.heritage_status === "to_clarify",
  },
  {
    key: "owner",
    label: "Propriétaire à vérifier",
    test: (a) => a.ownership_verification === "to_verify",
  },
  {
    key: "provenance",
    label: "Provenance non renseignée",
    test: (a) => !hasText(a.provenance),
  },
  {
    key: "proof",
    label: "Aucune pièce justificative (facture ou document)",
    test: (a) => a.invoice !== true && !hasDocuments(a),
  },
];

export default function CollectionHeritageAudit({ collectionId }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const txt = COPY[lang] || COPY.fr;
  const [nature, setNature] = useState(undefined);
  const [rows, setRows] = useState(undefined);

  useEffect(() => {
    let alive = true;
    if (!collectionId) { setRows([]); return undefined; }
    (async () => {
      try {
        const [{ data: col }, { data: arts }] = await Promise.all([
          supabase.from("collections").select("ensemble_nature").eq("id", collectionId).maybeSingle(),
          supabase
            .from("artworks")
            .select("id, title, artist, heritage_status, ownership_verification, provenance, invoice, documents")
            .eq("collection_id", collectionId)
            .limit(2000),
        ]);
        if (!alive) return;
        setNature(col?.ensemble_nature || null);
        setRows(Array.isArray(arts) ? arts : []);
      } catch {
        if (alive) { setNature(null); setRows([]); }
      }
    })();
    return () => { alive = false; };
  }, [collectionId]);

  const audit = useMemo(() => {
    const list = rows || [];
    return CHECKS.map((c) => ({ ...c, items: list.filter(c.test) }));
  }, [rows]);

  if (rows === undefined || nature === undefined) return null;
  const anyStatus = rows.some((a) => a.heritage_status);
  if (!nature && !anyStatus) return null;
  if (rows.length === 0) return null;

  const total = audit.reduce((s, c) => s + c.items.length, 0);

  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, background: "#faf9f7", marginBottom: 24 }}>
      <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: GOLD, marginBottom: 2 }}>
        {txt.eyebrow}
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "Georgia, serif" }}>
        {txt.title}
        {nature ? ` — ${txt.nature[nature] || nature}` : ""}
      </h3>
      <p style={{ fontSize: 12.5, color: "#8a8378", margin: "0 0 12px" }}>
        {total === 0
          ? txt.allGood
          : txt.subtitle}
      </p>

      {audit.map((c) => (
        c.items.length > 0 && (
          <details key={c.key} style={{ borderTop: `1px solid ${BORDER}`, padding: "8px 0" }}>
            <summary style={{ cursor: "pointer", fontSize: 13.5, color: "#1a1a1a", display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{txt.checks[c.key] || c.label}</span>
              <strong style={{ color: GOLD }}>{c.items.length} / {rows.length}</strong>
            </summary>
            <ul style={{ margin: "8px 0 4px", paddingLeft: 18, fontSize: 12.5, color: "#6b6459" }}>
              {c.items.slice(0, 8).map((a) => (
                <li key={a.id}>
                  <Link to={`/artwork/${a.id}`} style={{ color: "#1a1a1a", textDecoration: "underline" }}>
                    {[a.title, a.artist].filter(Boolean).join(" — ") || a.id}
                  </Link>
                </li>
              ))}
              {c.items.length > 8 && <li>{txt.more(c.items.length - 8)}</li>}
            </ul>
          </details>
        )
      ))}
    </section>
  );
}
